<script>
import DashboardStatisticsService from '@/services/DashboardStatisticsService'
import timerIcon from '@/assets/icons/dashboard/timer.svg?raw'
import qualityIcon from '@/assets/icons/dashboard/quality.svg?raw'
import starIcon from '@/assets/icons/dashboard/star.svg?raw'
import { themeSvgMarkup } from '@/utils/themeSvgUtils'

const footerIcons = Object.freeze({
  timer: themeSvgMarkup(timerIcon, { tintUnknownFills: true }),
  quality: themeSvgMarkup(qualityIcon, { tintUnknownFills: true }),
  star: themeSvgMarkup(starIcon, { tintUnknownFills: true })
})

export default {
  name: 'DashboardFooter',
  data() {
    return {
      viteCalendlyUrl: window.APP_CONFIG.VITE_CALENDLY_URL,
      footerIcons,
      activeProductCount: null,
      activeManufacturerCount: null
    }
  },
  computed: {
    productFooterText() {
      if (this.activeProductCount === null) {
        return `${this.$t('COMMON.NO_VALUE')} ${this.$t('DASHBOARD.FOOTER.PRODUCT_LABEL')}`
      }

      return `${this.formatCount(this.activeProductCount)} ${this.$t(
        'DASHBOARD.FOOTER.PRODUCT_LABEL'
      )}`
    },
    manufacturerFooterText() {
      if (this.activeManufacturerCount === null) {
        return `${this.$t('COMMON.NO_VALUE')} ${this.$t('DASHBOARD.FOOTER.MANUFACTURER_LABEL')}`
      }

      return `${this.formatCount(this.activeManufacturerCount)} ${this.$t(
        'DASHBOARD.FOOTER.MANUFACTURER_LABEL'
      )}`
    }
  },
  async created() {
    await this.loadProductManufacturerActiveCounts()
  },
  methods: {
    async loadProductManufacturerActiveCounts() {
      try {
        const response = await DashboardStatisticsService.getProductManufacturerActiveCounts()
        this.activeProductCount = response.active_product_count
        this.activeManufacturerCount = response.active_manufacturer_count
      } catch (_) {
        this.activeProductCount = null
        this.activeManufacturerCount = null
      }
    },
    formatCount(value) {
      return new Intl.NumberFormat(this.$i18n.locale).format(value)
    }
  }
}
</script>

<template>
  <v-footer app class="dashboard-footer">
    <div class="dashboard-footer-content">
      <div class="dashboard-footer-item">
        <span class="dashboard-footer-icon" aria-hidden="true" v-html="footerIcons.timer" />
        <p class="dashboard-footer-text">
          <span class="font-weight-bold pr-1">{{ $t('DASHBOARD.FOOTER.AVERAGE_CUSTOMER') }}</span>
          {{ $t('DASHBOARD.FOOTER.AVERAGE_RESPONSE') }}
        </p>
      </div>

      <div class="dashboard-footer-item">
        <span class="dashboard-footer-icon" aria-hidden="true" v-html="footerIcons.quality" />
        <p class="dashboard-footer-text">{{ $t('DASHBOARD.FOOTER.GUARANTEE') }}</p>
      </div>

      <div class="dashboard-footer-item">
        <span class="dashboard-footer-icon" aria-hidden="true" v-html="footerIcons.star" />
        <div>
          <p class="dashboard-footer-text font-weight-bold">{{ productFooterText }}</p>
          <p class="dashboard-footer-text">{{ manufacturerFooterText }}</p>
        </div>
      </div>

      <div class="dashboard-footer-action">
        <v-btn-secondary :href="viteCalendlyUrl" target="_blank" rel="noopener noreferrer">
          {{ $t('DASHBOARD.FOOTER.BUTTON') }}
        </v-btn-secondary>
      </div>
    </div>
  </v-footer>
</template>

<style scoped>
.dashboard-footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
}

.dashboard-footer-item {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 14px;
}

.dashboard-footer-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
}

.dashboard-footer-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}

.dashboard-footer-text {
  color: var(--default-font-color);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
}

.dashboard-footer-action {
  padding-inline-end: 72px;
}

@media (max-width: 959.98px) {
  .dashboard-footer-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .dashboard-footer-item {
    gap: 8px;
  }

  .dashboard-footer-icon {
    width: 28px;
    height: 28px;
  }

  .dashboard-footer-text {
    font-size: 12px;
    line-height: 16px;
  }

  .dashboard-footer-action :deep(.v-btn) {
    max-width: 100%;
    height: auto;
    min-height: 44px;
    padding: 8px 12px;
  }

  .dashboard-footer-action :deep(.v-btn__content) {
    white-space: normal;
  }
}

@media (max-width: 599.98px) {
  .dashboard-footer-action {
    grid-column: 1 / -1;
  }
}
</style>
